// Mock data for The Brain Theory

export const navLinks = [
  { label: 'Home', href: '#home', type: 'scroll' },
  { label: 'How It Works', href: '#how-it-works', type: 'scroll' },
  { label: 'Pricing', href: '#pricing', type: 'scroll' },
  { label: 'About Us', href: '/about', type: 'route' },
  { label: 'Help', href: '/help', type: 'route' },
];

export const heroStats = { count: '12,045', label: 'minds training daily' };

export const quickChallenges = [
  {
    id: 1, category: 'Logic',
    question: 'Which number completes the pattern?',
    prompt: '2, 6, 12, 20, 30, ?',
    options: ['42', '40', '44', '46'], answer: '42', seconds: 20,
  },
  {
    id: 2, category: 'Speed',
    question: 'Tap the odd one out \u2014 quickly!',
    prompt: 'Square \u2022 Circle \u2022 Cube \u2022 Triangle',
    options: ['Square', 'Circle', 'Cube', 'Triangle'], answer: 'Cube', seconds: 20,
  },
  {
    id: 3, category: 'Memory',
    question: 'Which shape was shown third?',
    prompt: 'Circle \u2192 Square \u2192 Triangle \u2192 Star',
    studyText: 'Memorise these shapes in order',
    memorizeSeconds: 5,
    options: ['Star', 'Circle', 'Triangle', 'Square'], answer: 'Triangle', seconds: 15,
  },
];

export const allCategories = [
  { key: 'Logic',     icon: 'Brain',      color: '#F4C430', desc: 'Solve puzzles and strengthen reasoning', exercises: 12 },
  { key: 'Memory',    icon: 'Lightbulb',  color: '#E8B923', desc: 'Boost recall and improve memory',       exercises: 10 },
  { key: 'Speed',     icon: 'Zap',        color: '#FFC94A', desc: 'Enhance focus and reaction time',       exercises:  8 },
  { key: 'Attention', icon: 'Target',     color: '#F4C430', desc: 'Train focus and mental clarity',        exercises:  9 },
  { key: 'Math',      icon: 'Calculator', color: '#E8B923', desc: 'Sharpen calculation and problem solving', exercises: 11 },
  { key: 'Mixed',     icon: 'Puzzle',     color: '#FFC94A', desc: 'A mix of all skills in one session',    exercises: 15 },
];

export const features = [
  { icon: 'CalendarClock', title: 'Daily Challenges', desc: 'New brain training puzzles every day' },
  { icon: 'BarChart3',     title: 'Track Progress',   desc: 'See your improvement in real time' },
  { icon: 'Flame',         title: 'Build Streaks',    desc: 'Consistency builds extraordinary minds' },
  { icon: 'Trophy',        title: 'Compete & Rank',   desc: 'Compete with the best and top the leaderboard' },
];

export const skills = allCategories.map((c) => ({
  icon: c.icon, title: c.key, color: c.color, desc: c.desc,
}));

export const howSteps = [
  { step: '01', title: 'Take the Snapshot',     desc: 'Answer three quick puzzles to reveal how your brain thinks today' },
  { step: '02', title: 'Get Your Plan',         desc: 'We personalize a fun daily program tuned to your strengths and gaps' },
  { step: '03', title: 'Train 5 Minutes a Day', desc: 'Short science-backed sessions that fit into any age or schedule' },
  { step: '04', title: 'Watch Yourself Evolve', desc: 'Track streaks, earn XP and climb the leaderboard' },
];

// Updated pricing per PRD Section 20.1: Explorer ₹49 / Challenger ₹99 / Master ₹149
export const pricing = [
  {
    name: 'Explorer', price: '\u20B949', period: 'per month',
    tagline: 'Begin your brain-training journey',
    features: [
      'Daily brain training',
      '5 core categories (Mixed locked)',
      'Personal Brain Score',
      'Streak tracker',
      'Basic progress dashboard',
    ],
    lockedCategories: ['Mixed'],
    cta: 'Choose Explorer', highlight: false,
  },
  {
    name: 'Challenger', price: '\u20B999', period: 'per month',
    tagline: 'For everyday brain athletes',
    features: [
      'Unlimited daily training',
      'Detailed progress tracking',
      'Adaptive difficulty engine',
      'Weekly & monthly leaderboards',
      'Priority customer support',
    ],
    cta: 'Choose Challenger', highlight: true,
  },
  {
    name: 'Master', price: '\u20B9149', period: 'per month',
    tagline: 'Unlock every advantage',
    features: [
      'Everything in Challenger',
      'Advanced insights & analytics',
      'Personalized weekly reports',
      'Premium exclusive challenges',
      'Concierge priority support',
    ],
    cta: 'Choose Master', highlight: false,
  },
];

export const testimonials = [
  { name: 'Ananya Rao',     role: 'College Student, 20',   quote: 'My focus during exams has changed completely! Five minutes a day is genuinely all it took', initial: 'A' },
  { name: 'Rahul Mehta',    role: 'Software Engineer, 34', quote: 'Feels like a workout for the brain \u2014 the daily streak keeps me hooked every morning', initial: 'R' },
  { name: 'Sunita Iyer',    role: 'Homemaker, 58',         quote: 'It is easy on the eyes and easy on the mind! My memory feels noticeably sharper', initial: 'S' },
  { name: 'Kabir Sharma',   role: 'School Student, 12',    quote: 'The puzzles are actually fun \u2014 I beat my dad on the leaderboard last week!', initial: 'K' },
  { name: 'Dr. Meera Nair', role: 'Neuroscientist',        quote: 'A thoughtful blend of gamification and cognitive science \u2014 genuinely well designed', initial: 'M' },
];

// ============================================================================
// ONBOARDING (per PRD Section 10.2)
// ============================================================================
export const onboardingQuestions = [
  {
    key: 'primary_goal',
    q: 'What would you like to improve the most?',
    options: [
      'Logical Thinking',
      'Memory',
      'Attention & Focus',
      'Speed',
      'Mathematical Thinking',
      'Overall Brain Performance',
    ],
  },
  {
    key: 'user_type',
    q: 'Which best describes you?',
    options: [
      'School Student',
      'College Student',
      'Working Professional',
      'Competitive Exam Aspirant',
      'Teacher',
      'Brain-Fitness Seeker',
    ],
  },
  {
    key: 'training_frequency',
    q: 'How often do you currently challenge your brain?',
    options: ['Daily', 'A few times a week', 'Occasionally', 'Rarely', 'This is my first time'],
  },
  {
    key: 'screen_time',
    q: 'What is your daily screen time on average?',
    options: ['Less than 2 hours', '2 \u2013 4 hours', '4 \u2013 6 hours', '6 \u2013 8 hours', 'More than 8 hours'],
  },
  {
    key: 'favourite_challenge',
    q: 'Which types of challenges excite you most?',
    hint: 'Pick as many as you like',
    multi: true,
    options: ['Logic', 'Memory', 'Attention', 'Speed', 'Math', 'Mixed / Not Sure'],
  },
  {
    key: 'personal_goal',
    q: 'What are your goals with The Brain Theory?',
    hint: 'Choose one or more',
    multi: true,
    options: [
      'Improve concentration',
      'Sharpen problem-solving',
      'Prepare for exams',
      'Keep my brain active',
      'Track cognitive improvement',
      'Just explore',
    ],
  },
];

// Dynamic personalization rules (PRD Section 10.4)
export const personalizationRules = [
  {
    match: (a) => ['6 \u2013 8 hours', 'More than 8 hours'].includes(a.screen_time),
    msg: 'You spend a significant amount of time on screens. The good news? It only takes a few minutes a day with The Brain Theory to build a healthier cognitive habit',
  },
  {
    match: (a) => a.user_type === 'Competitive Exam Aspirant',
    msg: 'We will prioritize challenges that strengthen analytical thinking and focus',
  },
  {
    match: (a) => a.user_type === 'Working Professional',
    msg: 'Preparing short daily sessions that fit your schedule',
  },
  {
    match: (a) => ['School Student', 'College Student'].includes(a.user_type),
    msg: 'Building a balanced training plan to improve learning and concentration',
  },
  {
    match: () => true,
    msg: 'Great start! We are preparing challenges tailored to your cognitive profile',
  },
];

export const brainProfileSteps = [
  'Analyzing your goals\u2026',
  'Understanding your learning style\u2026',
  'Evaluating your screen habits\u2026',
  'Personalizing your first challenges\u2026',
  'Preparing your Brain Snapshot\u2026',
];

// ============================================================================
// DASHBOARD MOCK DATA
// ============================================================================
export const dashboardData = {
  brainScore: 1250,
  brainScoreDelta: 120,
  streak: 12,
  bestStreak: 16,
  todayGoal: { done: 3, total: 5 },
  weeklyProgress: [
    { day: 'M', xp:  80 },
    { day: 'T', xp: 120 },
    { day: 'W', xp: 150 },
    { day: 'T', xp:  90 },
    { day: 'F', xp: 180 },
    { day: 'S', xp: 210 },
    { day: 'S', xp: 140 },
  ],
  strongestSkills: [
    { skill: 'Logic',    pct: 82 },
    { skill: 'Memory',   pct: 74 },
    { skill: 'Speed',    pct: 68 },
    { skill: 'Attention', pct: 65 },
  ],
  weakAreas: [
    { skill: 'Math',    pct: 44 },
    { skill: 'Mixed',   pct: 48 },
  ],
  suggested: [
    { title: 'Number Ladder',    category: 'Math',      duration: 6, difficulty: 'Medium' },
    { title: 'Sequence Recall',  category: 'Memory',    duration: 5, difficulty: 'Easy'   },
    { title: 'Reaction Sprint',  category: 'Speed',     duration: 3, difficulty: 'Hard'   },
  ],
  recentActivity: [
    { label: 'Completed \u201cLogic Builder\u201d',    score: '92%', when: '2h ago' },
    { label: 'Started \u201cMemory Matrix\u201d',       score: '\u2014',   when: 'Yesterday' },
    { label: '7-day streak milestone reached',       score: '+50 XP', when: '2 days ago' },
    { label: 'Completed \u201cReaction Sprint\u201d',   score: '88%', when: '3 days ago' },
  ],
};

// ============================================================================
// FAQ / ABOUT / CONTACT
// ============================================================================
export const faqs = [
  { q: 'Is The Brain Theory really for all ages?', a: 'Yes! From 8-year-olds to 80-year-olds \u2014 layouts, contrast and pacing adapt to keep it comfortable and fun for everyone' },
  { q: 'How much time do I need each day?',        a: 'Just 5 minutes a day is enough to see real improvement over a few weeks' },
  { q: 'Do I need to pay to start?',               a: 'Not at all! The Starter plan is free forever and includes daily challenges and progress tracking' },
  { q: 'Is my data safe?',                         a: 'Absolutely. Your data belongs to you and is never sold. You can export or delete it anytime' },
  { q: 'Which puzzle categories are included?',    a: 'Logic, Memory, Speed, Attention, Math and Mixed challenges \u2014 refreshed daily' },
  { q: 'Can I use it on my phone?',                a: 'Yes! The Brain Theory is fully responsive and optimised for mobile-first play' },
];

export const aboutHighlights = [
  { title: 'Our Mission', desc: 'Make brain training warm, welcoming and genuinely effective for every age' },
  { title: 'Our Method',  desc: 'Short, science-backed puzzles crafted with cognitive researchers' },
  { title: 'Our Promise', desc: 'A calm, kind experience \u2014 no dark patterns, no addictive tricks' },
];

export const contactInfo = {
  email: 'thebraintheoryofficial@gmail.com',
  whatsapp: '+91 97403 43133',
  whatsappUrl: 'https://wa.me/919740343133?text=' + encodeURIComponent('Hi, I need help with The Brain Theory'),
  hours: 'Mon \u2013 Sat  \u00b7  9:00 AM \u2013 7:00 PM IST',
};

// ============================================================================
// COUPON CODES (for Welcome Offer)
// ============================================================================
export const validCoupons = ['WELCOME', 'BRAIN30', 'WELCOME2026', 'MINDGIFT'];
